// src/features/media/components/VideoFilterPanel.tsx

import { useEffect, useState } from 'react';
import {
  Button,
  Group,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { DateTime } from 'luxon';
import { MediaUtils } from '../MediaUtils';
import { TagInput } from '@/shared/components/TagInput';
import { useTags } from '@/features/tag/useTags';
import { DURATION_FILTER_PRESETS, PUBLISHED_FILTER_PRESETS } from '@/lib/constants/media';
import type { VideoFilters } from '@/lib/types/Media';

interface VideoFilterPanelProps {
  filters: VideoFilters;
  onChange: (patch: Partial<VideoFilters>) => void;
}

const STATE_OPTIONS = [
  { label: 'Any', value: 'any' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

/** Epoch seconds (local midnight) → 'yyyy-MM-dd' string for DatePickerInput. */
function secondsToDateStr(seconds: number | null): string | null {
  if (seconds === null) return null;
  return DateTime.fromSeconds(seconds).toFormat('yyyy-MM-dd');
}

/** 'yyyy-MM-dd' picker value → epoch seconds at local midnight. */
function dateStrToSeconds(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const parsed = DateTime.fromFormat(dateStr, 'yyyy-MM-dd');
  return parsed.isValid ? Math.floor(parsed.toSeconds()) : null;
}

export function VideoFilterPanel({ filters, onChange }: VideoFilterPanelProps) {
  const { tags } = useTags();

  function handlePublishedPreset(days: number | null): void {
    const start = days === null
      ? DateTime.local().startOf('year')
      : DateTime.local().startOf('day').minus({ days });
    onChange({ publishedAfter: Math.floor(start.toSeconds()) });
  }

  return (
    <Paper withBorder p="md" radius="md">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl" verticalSpacing="lg">
        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Tags
          </Text>
          <TagInput
            label="Has all of"
            description=""
            placeholder="Select tags..."
            value={filters.tagsAll}
            onChange={(value) => onChange({ tagsAll: value })}
            allTags={tags}
            allowNewTags={false}
          />
          <TagInput
            label="Has any of"
            description=""
            placeholder="Select tags..."
            value={filters.tagsAny}
            onChange={(value) => onChange({ tagsAny: value })}
            allTags={tags}
            allowNewTags={false}
          />
          <TagInput
            label="Excludes"
            description=""
            placeholder="Select tags..."
            value={filters.tagsNone}
            onChange={(value) => onChange({ tagsNone: value })}
            allTags={tags}
            allowNewTags={false}
          />
        </Stack>

        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Published
          </Text>
          <DatePickerInput
            label="On or after"
            placeholder="Any date"
            clearable
            value={secondsToDateStr(filters.publishedAfter)}
            onChange={(value) => onChange({ publishedAfter: dateStrToSeconds(value) })}
          />
          <DatePickerInput
            label="Before"
            placeholder="Any date"
            clearable
            value={secondsToDateStr(filters.publishedBefore)}
            onChange={(value) => onChange({ publishedBefore: dateStrToSeconds(value) })}
          />
          <Group gap={6}>
            {PUBLISHED_FILTER_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="light"
                size="compact-xs"
                onClick={() => handlePublishedPreset(preset.days)}
              >
                {preset.label}
              </Button>
            ))}
          </Group>
        </Stack>

        <Stack gap="xs">
          <Text size="xs" fw={600} c="dimmed" tt="uppercase">
            Duration
          </Text>
          <Group grow>
            <TimecodeFilterInput
              label="Min"
              valueSeconds={filters.durationMin}
              onCommit={(seconds) => onChange({ durationMin: seconds })}
            />
            <TimecodeFilterInput
              label="Max"
              valueSeconds={filters.durationMax}
              onCommit={(seconds) => onChange({ durationMax: seconds })}
            />
          </Group>
          <Group gap={6}>
            {DURATION_FILTER_PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="light"
                size="compact-xs"
                onClick={() => onChange({ durationMin: preset.min, durationMax: preset.max })}
              >
                {preset.label}
              </Button>
            ))}
          </Group>

          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="sm">
            Status
          </Text>
          <SegmentedControl
            size="xs"
            w="fit-content"
            data={STATE_OPTIONS}
            value={filters.state ?? 'any'}
            onChange={(value) =>
              onChange({ state: value === 'any' ? null : (value as 'active' | 'inactive') })
            }
          />
          <Switch
            size="sm"
            label="Needs attention"
            description="Only videos that are not ready (errors, stuck ingest or processing)"
            checked={filters.needsAttention}
            onChange={(event) => onChange({ needsAttention: event.currentTarget.checked })}
          />
        </Stack>
      </SimpleGrid>
    </Paper>
  );
}

interface TimecodeFilterInputProps {
  label: string;
  valueSeconds: number | null;
  onCommit: (seconds: number | null) => void;
}

/**
 * Timecode input that commits on blur/Enter rather than per keystroke —
 * live parsing would apply transient values ("1:30" while typing "1:30:00").
 * Mirrors the committed value until the user starts a new draft.
 */
function TimecodeFilterInput({ label, valueSeconds, onCommit }: TimecodeFilterInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setDraft(null);
    setInvalid(false);
  }, [valueSeconds]);

  const display = draft ?? (valueSeconds !== null ? MediaUtils.formatTimecode(valueSeconds) : '');

  function commit(): void {
    if (draft === null) return;
    const trimmed = draft.trim();
    if (trimmed === '') {
      setDraft(null);
      setInvalid(false);
      onCommit(null);
      return;
    }
    const parsed = MediaUtils.parseTimecode(trimmed);
    if (parsed === null) {
      setInvalid(true);
      return;
    }
    setDraft(null);
    setInvalid(false);
    onCommit(parsed);
  }

  return (
    <TextInput
      label={label}
      placeholder="h:mm:ss"
      value={display}
      onChange={(event) => {
        setDraft(event.currentTarget.value);
        setInvalid(false);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') commit();
      }}
      error={invalid ? 'Use h:mm:ss' : undefined}
    />
  );
}
